import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Barra de rolagem própria (trilho + polegar), para containers que escondem
 * o scrollbar nativo e desenham o seu — mesma necessidade em mais de um
 * lugar: `AppMenuScrollArea` (menu/modal em flex) e qualquer lista de opções
 * de altura fixa (`SelectContent`, `MultipleChoiceChecklist`). O nativo não
 * pinta de forma consistente entre plataformas e, no touch, só aparece
 * durante o gesto — o produto precisa da barra sempre visível.
 *
 * A posição do polegar NÃO passa por `useState`: o `onScroll` de uma lista
 * longa dispara a cada frame do gesto, e gravar isso em estado do React
 * re-renderiza a lista inteira (aqui, até 40 `CheckboxField`) a cada frame —
 * esse trabalho de reconciliação compete com a rolagem por inércia do
 * WebKit e é o que causava o "pulo duplo" ao arrastar até o fim no iOS. O
 * estilo do polegar é escrito direto no DOM via `thumbRef`; só a visibilidade
 * (que muda raramente — não a cada pixel rolado) passa por estado.
 */

const TRACK_INSET = 2
const THUMB_MIN_HEIGHT = 24
const OVERFLOW_THRESHOLD = 1

export function useScrollThumb<T extends HTMLElement = HTMLDivElement>(
  watch: unknown
) {
  const ref = useRef<T>(null)
  const thumbRef = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  const update = useCallback(() => {
    const node = ref.current
    if (!node) return

    const { scrollHeight, clientHeight, scrollTop } = node
    const overflow = scrollHeight - clientHeight
    const nextVisible = overflow > OVERFLOW_THRESHOLD

    setVisible(current => (current === nextVisible ? current : nextVisible))

    if (!nextVisible) return

    const trackHeight = clientHeight - TRACK_INSET * 2
    const thumbHeight = Math.max(
      THUMB_MIN_HEIGHT,
      (clientHeight / scrollHeight) * trackHeight
    )
    const maxThumbTop = Math.max(trackHeight - thumbHeight, 0)
    const top = (scrollTop / overflow) * maxThumbTop

    const thumb = thumbRef.current
    if (thumb) {
      thumb.style.height = `${thumbHeight}px`
      thumb.style.transform = `translateY(${top}px)`
    }
  }, [])

  useEffect(() => {
    const node = ref.current
    if (!node) return undefined

    // Três gatilhos, porque medir só no mount não cobre o ciclo de vida real
    // de um popover:
    //
    // 1. Próximos quadros — quando o `SelectContent` monta, o Radix ainda não
    //    posicionou o popover nem definiu
    //    `--radix-select-content-available-height`, então a lista ainda não
    //    está limitada: medir agora dá "não transborda", e a barra nascia
    //    escondida até o primeiro gesto de rolagem. Dois quadros porque o
    //    posicionamento do Radix acontece depois do layout, não durante.
    // 2. `ResizeObserver` — a caixa da área rolável mudando de tamanho
    //    (viewport girou, popover reposicionou, fonte carregou).
    // 3. `MutationObserver` — item entrando ou saindo da lista muda o
    //    `scrollHeight` sem mudar a caixa (ela está presa no `max-height`),
    //    e nesse caso o `ResizeObserver` não acorda sozinho.
    update()

    let secondFrame = 0
    const firstFrame = requestAnimationFrame(() => {
      update()
      secondFrame = requestAnimationFrame(update)
    })

    const resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(node)

    const mutationObserver = new MutationObserver(update)
    mutationObserver.observe(node, { childList: true, subtree: true })

    return () => {
      cancelAnimationFrame(firstFrame)
      cancelAnimationFrame(secondFrame)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `watch` é o gatilho deliberado (conteúdo que muda a altura), não uma dependência de closure
  }, [watch, update])

  // Callback ref, não o `thumbRef` cru: o polegar só entra no DOM no render
  // que `visible` dispara, um passo depois da medição que o tornou visível —
  // naquele momento `thumbRef.current` ainda era `null` e a altura calculada
  // não foi aplicada em ninguém. Sem remedir aqui, o polegar nasce com altura
  // zero e só aparece no primeiro gesto de rolagem.
  const setThumb = useCallback(
    (node: HTMLDivElement | null) => {
      thumbRef.current = node
      if (node) update()
    },
    [update]
  )

  return { ref, thumbRef: setThumb, visible, onScroll: update }
}
